// src/features/chat/useEntityDiscussionBadge.ts
// Unread count across the discussions parented to an entity — the badge on the
// rail's Discussion tab. The REST binding ignores `parent` in the query, so
// scoping is client-side (same as EntityChatPanel, see framework-4xt6).

import { useEffect, useState } from 'react';
import { ListConversations } from '@core/api/endpoints/chat/list_conversations';
import { GetUnreadCounts } from '@core/api/endpoints/chat/get_unread_counts';
import type { EntityContext } from '@/stores/EntityStore';

export function useEntityDiscussionBadge(ctx: EntityContext | null): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!ctx?.id) { setCount(0); return; }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await ListConversations({
          parent: { id: ctx.id, type_name: ctx.type },
          pagination: { cursor: '', limit: 20, direction: 'forward' },
        } as any);
        const raw: any = res?.conversations;
        const list: any[] = Array.isArray(raw) ? raw : (raw?.conversations ?? []);
        const ids = new Set(
          list
            .filter((c: any) => c?.parent?.id === ctx.id && c?.parent?.type_name === ctx.type)
            .map((c: any) => c?.entity?.id ?? c?.id)
            .filter(Boolean),
        );
        if (ids.size === 0) { if (!cancelled) setCount(0); return; }
        const counts: any = await GetUnreadCounts({} as any);
        const total = (counts?.counts ?? [])
          .filter((c: any) => ids.has(c?.conversation?.id))
          .reduce((sum: number, c: any) => sum + (c?.unread_count ?? 0), 0);
        if (!cancelled) setCount(total);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [ctx?.id, ctx?.type]);

  return count;
}
